export type SeoProps = {
	title: string;
	description: string;
	path: string;
	robots?: string;
	ogType?: "website" | "article";
};

export type ResolvedSeo = {
	title: string;
	description: string;
	robots: string;
	ogType: "website" | "article";
	url: string;
	image: string;
	siteName: string;
};

export const SEO_SITE_ORIGIN = "https://clerical.com";
export const SEO_DEFAULT_ROBOTS =
	"index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
export const SEO_OG_IMAGE_URL = `${SEO_SITE_ORIGIN}/apple-touch-icon.png`;
export const SEO_SITE_NAME = "Clerical";

export const SEO_ROUTE_PROPS = {
	"/": {
		title: "Clerical — AI Automation for Immigration Law Firms",
		description:
			"Clerical helps immigration law firms automate intake, document collection, client follow-ups, case monitoring, and filing prep with AI experts.",
		path: "/",
	},
	"/privacy": {
		title: "Privacy Policy | Clerical",
		description:
			"Read the Clerical Privacy Policy covering how we collect, use, and safeguard information related to our services.",
		path: "/privacy",
	},
	"/terms": {
		title: "Terms of Service | Clerical",
		description:
			"Read the Clerical Terms of Service governing access to and use of Clerical's automation services.",
		path: "/terms",
	},
	"/support": {
		title: "Support | Clerical",
		description:
			"Get help with Clerical — contact support, troubleshoot the Clerical Outlook add-in, and find answers to common questions.",
		path: "/support",
	},
	"/changelog": {
		title: "Changelog | Clerical",
		description:
			"New features and product updates from the Clerical team, including the latest improvements to Clerical's AI automation for immigration law firms.",
		path: "/changelog",
	},
} as const satisfies Record<string, SeoProps>;

export const PRERENDER_ROUTES = Object.freeze(Object.keys(SEO_ROUTE_PROPS));

function canonicalUrl(path: string) {
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	return new URL(normalizedPath, SEO_SITE_ORIGIN).toString();
}

export function resolveSeo({
	title,
	description,
	path,
	robots = SEO_DEFAULT_ROBOTS,
	ogType = "website",
}: SeoProps): ResolvedSeo {
	return {
		title,
		description,
		robots,
		ogType,
		url: canonicalUrl(path),
		image: SEO_OG_IMAGE_URL,
		siteName: SEO_SITE_NAME,
	};
}

function normalizePath(pathname: string) {
	if (pathname === "/") return pathname;
	return pathname.replace(/\/+$/, "") || "/";
}

export function seoPropsForPath(pathname: string): SeoProps {
	const path = normalizePath(pathname);
	if (path in SEO_ROUTE_PROPS) {
		return SEO_ROUTE_PROPS[path as keyof typeof SEO_ROUTE_PROPS];
	}
	return SEO_ROUTE_PROPS["/"];
}

export function resolvedSeoForPath(pathname: string): ResolvedSeo {
	return resolveSeo(seoPropsForPath(pathname));
}
